// Venue space-style vocabulary shared by the image_qa v17 prompt block, the
// /references venue_style filter, the store-time vendor facet aggregation, and
// the web chips (0029). Superset of the venue pack's SPACE_STYLES (CISC item
// axis) extended with the PRD §9.4 setting tiles (beach/estate/waterfront)
// plus 'other' as the model's escape hatch.

export const VENUE_STYLE_VOCAB = [
  "loft", "ballroom", "garden", "industrial", "rooftop", "barn", "modern",
  "historic", "beach", "estate", "waterfront", "other",
] as const;

export type VenueStyle = (typeof VENUE_STYLE_VOCAB)[number];

/** The filterable values — everything except the 'other' escape hatch. */
export const VENUE_STYLE_FILTERABLE = VENUE_STYLE_VOCAB.filter(
  (s): s is Exclude<VenueStyle, "other"> => s !== "other",
);

export const venueStyleLabel = (style: string): string =>
  style.charAt(0).toUpperCase() + style.slice(1);

/**
 * Deterministic setting→aesthetic mapping: a venue's physical setting implies
 * BriefStyle aesthetics for matching/facets. Values are BriefStyle enum
 * members (schemas.ts); kept as plain strings to avoid a zod import cycle.
 */
export const SETTING_TO_BRIEF_STYLE: Record<VenueStyle, string[]> = {
  loft: ["industrial", "modern"],
  ballroom: ["classic", "luxury"],
  garden: ["garden"],
  industrial: ["industrial"],
  rooftop: ["modern"],
  barn: ["rustic"],
  modern: ["modern", "minimal"],
  historic: ["vintage", "classic"],
  beach: ["tropical"],
  estate: ["classic", "garden"],
  waterfront: [],
  other: [],
};

/**
 * Inverse of `SETTING_TO_BRIEF_STYLE`: a couple's chosen décor aesthetic
 * (`BriefStyle`, captured in the builder's Foundations / "find my venue" step)
 * → the venue settings that imply it. Lets the builder's venue/style step
 * PRESELECT a `venue_style` chip from `brief.styles`. Derived from the forward
 * map so the two never drift; values are `VenueStyle`s (the `venue_style` chip
 * vocab). A BriefStyle with no mapped setting (e.g. 'boho') is simply absent.
 */
export const BRIEF_STYLE_TO_VENUE_STYLE: Record<string, VenueStyle[]> = (() => {
  const out: Record<string, VenueStyle[]> = {};
  for (const [setting, briefStyles] of Object.entries(SETTING_TO_BRIEF_STYLE)) {
    for (const bs of briefStyles) (out[bs] ??= []).push(setting as VenueStyle);
  }
  return out;
})();
